// shop-frontend/src/pages/ShopSettings.js
import React, { useState, useEffect, useCallback } from "react";
import { useShopAuth } from "../context/ShopAuthContext";
import { getShopMe, apiPut, request, toAbsUrl, shopSavePaymentSettings, getShopPublicUrl } from "../utils/api";
import { Settings, User, Bell, Upload, Save, LogOut, Camera, Sun, Moon, Pencil, X, MapPin, Wallet, Copy, ExternalLink, CreditCard } from "lucide-react";
import "../styles/ShopSettings.css";
import { COUNTRIES as SHOP_COUNTRIES } from "../utils/countries";

export default function ShopSettings() {
  const { logout, theme, setTheme, refreshMe } = useShopAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // ✅ Edit mode for profile (default: read-only)
  const [editing, setEditing] = useState(false);

  // Profile data (source of truth from backend)
  const [shopData, setShopData] = useState(null);

  // Editable fields (only used when editing)
  const [shopName, setShopName] = useState("");
  const [bio, setBio] = useState("");
  const [phone, setPhone] = useState("");
  const [website, setWebsite] = useState("");
  const [country, setCountry] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [coverImage, setCoverImage] = useState("");

  // Notification preferences
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [orderNotifications, setOrderNotifications] = useState(true);
  const [messageNotifications, setMessageNotifications] = useState(true);
  const [reviewNotifications, setReviewNotifications] = useState(true);

  // Blockchain wallet
  const [walletAddress, setWalletAddress] = useState("");
  const [walletCopied, setWalletCopied] = useState(false);

  // Shipping address fields
  const [address, setAddress] = useState({
    name: "", company: "", street1: "", street2: "",
    city: "", state: "", postalCode: "", country: "US", phone: "",
  });

  // Payment settings
  const [paypalEmail, setPaypalEmail] = useState("");
  const [acceptCash, setAcceptCash] = useState(false);
  const [meetupAddress, setMeetupAddress] = useState("");

  // Public shop URL (read-only — derived from the shop's handle; not editable here)
  const [shopPublicUrl, setShopPublicUrl] = useState("");
  const [urlCopied, setUrlCopied] = useState(false);

  // Active tab
  const [activeTab, setActiveTab] = useState("profile");

  const hydrateFromShop = useCallback((s) => {
    if (!s) return;
    setShopData(s);
    setShopName(s.shopName || s.name || "");
    setBio(s.bio || "");
    setPhone(s.phone || "");
    setWebsite(s.website || "");
    setCountry(s.country || "");
    setLogoUrl(s.logoUrl || s.logo || "");
    setCoverImage(s.coverImage || "");
    setWalletAddress(s.blockchainWallet?.address || "");
    setEmailNotifications(s.notifications?.email !== false);
    setOrderNotifications(s.notifications?.orders !== false);
    setMessageNotifications(s.notifications?.messages !== false);
    setReviewNotifications(s.notifications?.reviews !== false);
    if (s.address && typeof s.address === "object") {
      setAddress({
        name: s.address.name || "",
        company: s.address.company || "",
        street1: s.address.street1 || "",
        street2: s.address.street2 || "",
        city: s.address.city || "",
        state: s.address.state || "",
        postalCode: s.address.postalCode || "",
        country: s.address.country || "US",
        phone: s.address.phone || "",
      });
    }
    setPaypalEmail(s.paymentSettings?.paypalEmail || "");
    setAcceptCash(!!s.paymentSettings?.acceptCash);
    setMeetupAddress(s.paymentSettings?.meetupAddress || "");
    setShopPublicUrl(getShopPublicUrl(s));
  }, []);

  const loadShopData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const data = await getShopMe();
      const s = data?.shop || data;
      hydrateFromShop(s);
    } catch (err) {
      setError(err?.message || "Failed to load shop data");
    } finally {
      setLoading(false);
    }
  }, [hydrateFromShop]);

  useEffect(() => {
    loadShopData();
  }, [loadShopData]);

  function startEditing() {
    // Reset edit fields from current data
    if (shopData) hydrateFromShop(shopData);
    setEditing(true);
  }

  function cancelEditing() {
    if (shopData) hydrateFromShop(shopData);
    setEditing(false);
    setError("");
  }

  async function handleSaveProfile() {
    try {
      setSaving(true);
      setError("");
      setSuccess("");

      const updates = { shopName, bio, phone, website, country };
      const res = await apiPut("/shop-profile/me", updates);
      const updated = res?.shop || res;

      // ✅ Update local state with server response
      hydrateFromShop(updated);
      setEditing(false);

      // ✅ Refresh auth context so sidebar/header shows updated data
      if (refreshMe) await refreshMe();

      setSuccess("✅ Profile updated!");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err?.message || "Failed to save profile");
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveNotifications() {
    try {
      setSaving(true);
      setError("");
      setSuccess("");

      const notifications = {
        email: emailNotifications,
        orders: orderNotifications,
        messages: messageNotifications,
        reviews: reviewNotifications,
      };

      await apiPut("/shop-profile/me/notifications", { notifications });

      // ✅ Refresh context
      if (refreshMe) await refreshMe();

      setSuccess("✅ Notification preferences saved!");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err?.message || "Failed to save notification preferences");
    } finally {
      setSaving(false);
    }
  }

  async function handleLogoUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploading(true);
      setError("");

      const formData = new FormData();
      formData.append("shopLogo", file);

      const result = await request("/shop-profile/me/logo", {
        method: "PUT",
        body: formData,
        auth: true,
      });

      const newUrl = result?.logoUrl || result?.shop?.logoUrl || "";
      setLogoUrl(newUrl);
      setShopData((prev) => (prev ? { ...prev, logoUrl: newUrl } : prev));

      if (refreshMe) await refreshMe();

      setSuccess("✅ Logo uploaded!");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err?.message || "Failed to upload logo");
    } finally {
      setUploading(false);
    }
  }

  async function handleCoverUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploading(true);
      setError("");

      const formData = new FormData();
      formData.append("cover", file);

      const result = await request("/shop-profile/me/cover", {
        method: "PUT",
        body: formData,
        auth: true,
      });

      const newUrl = result?.coverImage || result?.shop?.coverImage || "";
      setCoverImage(newUrl);
      setShopData((prev) => (prev ? { ...prev, coverImage: newUrl } : prev));

      if (refreshMe) await refreshMe();

      setSuccess("✅ Cover uploaded!");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err?.message || "Failed to upload cover image");
    } finally {
      setUploading(false);
    }
  }

  if (loading) {
    return (
      <div className="sset-wrap">
        <div className="sset-h1">⚙️ Settings</div>
        <div className="sset-card">
          <div className="sset-note">Loading shop data...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="sset-wrap">
      <div className="sset-header">
        <div className="sset-header-left">
          <Settings size={28} />
          <div className="sset-h1">Settings</div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="sset-tabs">
        <button
          className={activeTab === "profile" ? "sset-tab active" : "sset-tab"}
          onClick={() => setActiveTab("profile")}
        >
          <User size={18} />
          Profile
        </button>
        <button
          className={activeTab === "notifications" ? "sset-tab active" : "sset-tab"}
          onClick={() => setActiveTab("notifications")}
        >
          <Bell size={18} />
          Notifications
        </button>
        <button
          className={activeTab === "address" ? "sset-tab active" : "sset-tab"}
          onClick={() => setActiveTab("address")}
        >
          <MapPin size={18} />
          Address
        </button>
        <button
          className={activeTab === "appearance" ? "sset-tab active" : "sset-tab"}
          onClick={() => setActiveTab("appearance")}
        >
          <Sun size={18} />
          Appearance
        </button>
        <button
          className={activeTab === "payments" ? "sset-tab active" : "sset-tab"}
          onClick={() => setActiveTab("payments")}
        >
          <CreditCard size={18} />
          Payments
        </button>
      </div>

      {/* Messages */}
      {error && <div className="sset-error">❌ {error}</div>}
      {success && <div className="sset-success">{success}</div>}

      {/* ═══════════════════ PROFILE TAB ═══════════════════ */}
      {activeTab === "profile" && (
        <div className="sset-section">

          {/* 🔗 Your Moondala Link */}
          {shopPublicUrl && (
            <div className="sset-card sset-shopurl-card">
              <div className="sset-card-title"><span style={{ fontSize: 20 }}>🔗</span> Your Moondala Link</div>
              <div className="sset-shopurl-preview">
                <a href={shopPublicUrl} target="_blank" rel="noopener noreferrer" className="sset-shopurl-url">{shopPublicUrl}</a>
                <button className={`sset-small-btn ${urlCopied ? "sset-copied" : ""}`} onClick={() => { navigator.clipboard.writeText(shopPublicUrl); setUrlCopied(true); setTimeout(() => setUrlCopied(false), 2500); }}>{urlCopied ? "✓ Copied" : "Copy"}</button>
                <a href={`https://wa.me/?text=${encodeURIComponent("Check out my shop on Moondala: " + shopPublicUrl)}`} target="_blank" rel="noopener noreferrer" className="sset-small-btn" style={{ textDecoration: "none" }}>Share</a>
              </div>
            </div>
          )}

          {/* Logo + Cover */}
          <div className="sset-card">
            <div className="sset-card-title">
              <Camera size={20} />
              Shop Images
            </div>

            <div className="sset-images-row">
              {/* Logo */}
              <div className="sset-avatar-section">
                <div className="sset-avatar-preview">
                  {logoUrl ? (
                    <img
                      src={toAbsUrl(logoUrl)}
                      alt="Shop Logo"
                      className="sset-avatar-img"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  ) : (
                    <div className="sset-avatar-placeholder">
                      {(shopName || "S").charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
                <label className="sset-upload-btn">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    style={{ display: "none" }}
                    disabled={uploading}
                  />
                  <Upload size={16} />
                  {uploading ? "Uploading..." : "Change Logo"}
                </label>
              </div>

              {/* Cover */}
              <div style={{ flex: 1 }}>
                {coverImage ? (
                  <img
                    src={toAbsUrl(coverImage)}
                    alt="Cover"
                    className="sset-cover-preview"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                ) : (
                  <div className="sset-cover-placeholder">No cover image</div>
                )}
                <label className="sset-upload-btn" style={{ marginTop: 8 }}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleCoverUpload}
                    style={{ display: "none" }}
                    disabled={uploading}
                  />
                  <Upload size={16} />
                  {uploading ? "Uploading..." : "Change Cover"}
                </label>
              </div>
            </div>
          </div>

          {/* Web3 Wallet */}
          <div className="sset-card">
            <div className="sset-card-title">
              <Wallet size={20} />
              Web3 Wallet
            </div>
            {walletAddress ? (
              <div className="sset-readonly">
                <div className="sset-ro-row" style={{ alignItems: "flex-start" }}>
                  <div className="sset-ro-label">Wallet Address</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, justifyContent: "flex-end", flexWrap: "wrap" }}>
                    <span className="sset-mono" style={{ fontSize: 12, wordBreak: "break-all" }}>{walletAddress}</span>
                    <button
                      title="Copy address"
                      onClick={() => {
                        navigator.clipboard.writeText(walletAddress).catch(() => {});
                        setWalletCopied(true);
                        setTimeout(() => setWalletCopied(false), 2000);
                      }}
                      style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-secondary, #aaa)", padding: 0 }}
                    >
                      <Copy size={14} />
                    </button>
                    <a
                      href={`https://basescan.org/address/${walletAddress}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="View on BaseScan"
                      style={{ color: "var(--text-secondary, #aaa)" }}
                    >
                      <ExternalLink size={14} />
                    </a>
                    {walletCopied && <span style={{ fontSize: 11, color: "#4ade80" }}>Copied!</span>}
                  </div>
                </div>
                <div className="sset-ro-row">
                  <div className="sset-ro-label">Network</div>
                  <div className="sset-ro-value">Base</div>
                </div>
              </div>
            ) : (
              <div className="sset-note" style={{ color: "var(--text-secondary, #aaa)", fontSize: 14, padding: "12px 0" }}>
                ⏳ Wallet is being created... Check back in a moment.
              </div>
            )}
          </div>

          {/* Shop Information — READ-ONLY by default */}
          <div className="sset-card">
            <div className="sset-card-title-row">
              <div className="sset-card-title">
                <User size={20} />
                Shop Information
              </div>
              {!editing ? (
                <button className="sset-edit-btn" onClick={startEditing} title="Edit profile">
                  <Pencil size={16} />
                  Edit
                </button>
              ) : (
                <button className="sset-cancel-btn" onClick={cancelEditing} title="Cancel editing">
                  <X size={16} />
                  Cancel
                </button>
              )}
            </div>

            {!editing ? (
              /* ═══ READ-ONLY VIEW ═══ */
              <div className="sset-readonly">
                <ReadOnlyRow label="Shop Name" value={shopData?.shopName || shopData?.name} />
                <ReadOnlyRow label="Bio" value={shopData?.bio} />
                <ReadOnlyRow label="Email" value={shopData?.shopEmail || shopData?.email} />
                <ReadOnlyRow label="Owner" value={shopData?.ownerName || [shopData?.ownerFirstName, shopData?.ownerLastName].filter(Boolean).join(" ")} />
                <ReadOnlyRow label="Phone" value={shopData?.phone} />
                <ReadOnlyRow label="Website" value={shopData?.website} />
                <ReadOnlyRow label="Country" value={shopData?.country} />
                <ReadOnlyRow label="Shop ID" value={shopData?._id || shopData?.id} mono />
              </div>
            ) : (
              /* ═══ EDIT VIEW ═══ */
              <>
                <div className="sset-form-group">
                  <label className="sset-label">Shop Name</label>
                  <input
                    type="text"
                    value={shopName}
                    onChange={(e) => setShopName(e.target.value)}
                    className="sset-input"
                    placeholder="Enter shop name"
                  />
                </div>

                <div className="sset-form-group">
                  <label className="sset-label">Bio / Description</label>
                  <textarea
                    value={bio}
                    onChange={(e) => setBio(e.target.value)}
                    className="sset-textarea"
                    placeholder="Tell customers about your shop..."
                    rows={4}
                  />
                </div>

                <div className="sset-form-group">
                  <label className="sset-label">Phone Number</label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="sset-input"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>

                <div className="sset-form-group">
                  <label className="sset-label">Website</label>
                  <input
                    type="url"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    className="sset-input"
                    placeholder="https://yourshop.com"
                  />
                </div>

                <div className="sset-form-group">
                  <label className="sset-label">Country</label>
                  <input
                    type="text"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    className="sset-input"
                    placeholder="e.g. Yemen, USA, UK"
                  />
                </div>

                <button
                  className="sset-save-btn"
                  onClick={handleSaveProfile}
                  disabled={saving}
                >
                  <Save size={18} />
                  {saving ? "Saving..." : "Save Profile"}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* ═══════════════════ NOTIFICATIONS TAB ═══════════════════ */}
      {activeTab === "notifications" && (
        <div className="sset-section">
          <div className="sset-card">
            <div className="sset-card-title">
              <Bell size={20} />
              Notification Preferences
            </div>

            <div className="sset-notif-hint">
              Choose how you want to be notified about shop activity
            </div>

            <div className="sset-switch-group">
              <SwitchRow
                label="Email Notifications"
                desc="Receive updates via email"
                checked={emailNotifications}
                onChange={setEmailNotifications}
              />
              <SwitchRow
                label="Order Notifications"
                desc="Get notified about new orders"
                checked={orderNotifications}
                onChange={setOrderNotifications}
              />
              <SwitchRow
                label="Message Notifications"
                desc="Alert when customers message you"
                checked={messageNotifications}
                onChange={setMessageNotifications}
              />
              <SwitchRow
                label="Review Notifications"
                desc="Notify when customers leave reviews"
                checked={reviewNotifications}
                onChange={setReviewNotifications}
              />
            </div>

            <button
              className="sset-save-btn"
              onClick={handleSaveNotifications}
              disabled={saving}
            >
              <Save size={18} />
              {saving ? "Saving..." : "Save Preferences"}
            </button>
          </div>
        </div>
      )}

      {/* ═══════════════════ ADDRESS TAB ═══════════════════ */}
      {activeTab === "address" && (
        <div className="sset-section">
          <div className="sset-card">
            <div className="sset-card-title">
              <MapPin size={20} />
              Shipping Address
            </div>
            <div className="sset-upload-hint" style={{ marginBottom: 16 }}>
              This address is used as the origin for shipping rate calculations.
            </div>

            <div className="sset-form-group">
              <label className="sset-label">Name (on label)</label>
              <input type="text" value={address.name} onChange={(e) => setAddress(a => ({ ...a, name: e.target.value }))} className="sset-input" placeholder="John Doe" />
            </div>
            <div className="sset-form-group">
              <label className="sset-label">Company (optional)</label>
              <input type="text" value={address.company} onChange={(e) => setAddress(a => ({ ...a, company: e.target.value }))} className="sset-input" placeholder="Acme Inc." />
            </div>
            <div className="sset-form-group">
              <label className="sset-label">Street Address 1</label>
              <input type="text" value={address.street1} onChange={(e) => setAddress(a => ({ ...a, street1: e.target.value }))} className="sset-input" placeholder="123 Main St" />
            </div>
            <div className="sset-form-group">
              <label className="sset-label">Street Address 2 (optional)</label>
              <input type="text" value={address.street2} onChange={(e) => setAddress(a => ({ ...a, street2: e.target.value }))} className="sset-input" placeholder="Suite 4B" />
            </div>
            <div style={{ display: "flex", gap: 12 }}>
              <div className="sset-form-group" style={{ flex: 2 }}>
                <label className="sset-label">City</label>
                <input type="text" value={address.city} onChange={(e) => setAddress(a => ({ ...a, city: e.target.value }))} className="sset-input" placeholder="New York" />
              </div>
              <div className="sset-form-group" style={{ flex: 1 }}>
                <label className="sset-label">State</label>
                <input type="text" value={address.state} onChange={(e) => setAddress(a => ({ ...a, state: e.target.value.toUpperCase().slice(0, 2) }))} className="sset-input" placeholder="NY" maxLength={2} />
              </div>
            </div>
            <div style={{ display: "flex", gap: 12 }}>
              <div className="sset-form-group" style={{ flex: 1 }}>
                <label className="sset-label">Postal / ZIP Code</label>
                <input type="text" value={address.postalCode} onChange={(e) => setAddress(a => ({ ...a, postalCode: e.target.value }))} className="sset-input" placeholder="10001" />
              </div>
              <div className="sset-form-group" style={{ flex: 1 }}>
                <label className="sset-label">Country</label>
                <select value={address.country} onChange={(e) => setAddress(a => ({ ...a, country: e.target.value }))} className="sset-input">
                  {SHOP_COUNTRIES.map((c) => (
                    <option key={c.code} value={c.code}>{c.name} ({c.code})</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="sset-form-group">
              <label className="sset-label">Phone</label>
              <input type="tel" value={address.phone} onChange={(e) => setAddress(a => ({ ...a, phone: e.target.value }))} className="sset-input" placeholder="+1 (555) 123-4567" />
            </div>

            <button
              className="sset-save-btn"
              onClick={async () => {
                try {
                  setSaving(true);
                  setError("");
                  setSuccess("");
                  const res = await apiPut("/shop-profile/me", { address });
                  const updated = res?.shop || res;
                  hydrateFromShop(updated);
                  if (refreshMe) await refreshMe();
                  setSuccess("✅ Shipping address saved!");
                  setTimeout(() => setSuccess(""), 3000);
                } catch (err) {
                  setError(err?.message || "Failed to save address");
                } finally {
                  setSaving(false);
                }
              }}
              disabled={saving}
            >
              <Save size={18} />
              {saving ? "Saving..." : "Save Address"}
            </button>
          </div>
        </div>
      )}

      {/* ═══════════════════ APPEARANCE TAB ═══════════════════ */}
      {activeTab === "appearance" && (
        <div className="sset-section">
          <div className="sset-card">
            <div className="sset-card-title">
              <Sun size={20} />
              Theme
            </div>

            <div className="sset-form-group">
              <label className="sset-label">Choose Theme</label>
              <div className="sset-theme-buttons">
                <button
                  onClick={() => setTheme("dark")}
                  className={`sset-theme-btn ${theme === "dark" ? "active" : ""}`}
                >
                  <Moon size={20} />
                  <span>Dark Mode</span>
                </button>
                <button
                  onClick={() => setTheme("light")}
                  className={`sset-theme-btn ${theme === "light" ? "active" : ""}`}
                >
                  <Sun size={20} />
                  <span>Light Mode</span>
                </button>
              </div>
              <div className="sset-upload-hint">
                Theme changes apply immediately across the app
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PAYMENTS TAB */}
      {activeTab === "payments" && (
        <div className="sset-section">
          <div className="sset-card">
            <div className="sset-card-title"><CreditCard size={20} /> Payment Options</div>
            <p className="sset-upload-hint" style={{ marginBottom: 20 }}>Choose how Moondala sends you your earnings.</p>
            <div className="sset-form-group">
              <label className="sset-label">🅿️ PayPal Payouts</label>
              <p style={{ fontSize: 13, color: "#6b7280", margin: "0 0 8px" }}>Get your earnings sent to your PayPal account — ideal where Stripe isn't available. Enter the email on your PayPal account.</p>
              <input type="email" className="sset-input" placeholder="your-paypal@email.com" value={paypalEmail} onChange={(e) => setPaypalEmail(e.target.value)} />
              {paypalEmail.trim() && (
                <p style={{ fontSize: 13, color: "#16a34a", margin: "8px 0 0" }}>✅ Payouts will be sent to {paypalEmail.trim()}</p>
              )}
            </div>
            <div className="sset-divider" />
            <div className="sset-form-group">
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                <div><label className="sset-label">💵 Accept Cash Payments</label><p style={{ fontSize: 13, color: "#6b7280", margin: 0 }}>Customers pay in person. You arrange the meetup.</p></div>
                <label className="switch"><input type="checkbox" checked={acceptCash} onChange={(e) => setAcceptCash(e.target.checked)} /><span className="slider" /></label>
              </div>
              {acceptCash && (
                <div className="sset-form-group" style={{ marginTop: 12 }}>
                  <label className="sset-label">📍 Meetup Address</label>
                  <textarea className="sset-textarea" rows={3} placeholder={`e.g. Downtown Coffee Shop, 123 Main St\nAvailable Mon–Sat 10am–6pm`} value={meetupAddress} onChange={(e) => setMeetupAddress(e.target.value)} />
                </div>
              )}
            </div>
            <button className="sset-save-btn" disabled={saving} onClick={async () => {
              try { setSaving(true); setError(""); setSuccess(""); const oldPaypal = String(shopData?.paymentSettings?.paypalEmail || "").trim().toLowerCase();
                // Changing the payout email needs the shop password (server checks it).
                let pw = "";
                if (paypalEmail.trim().toLowerCase() !== oldPaypal) {
                  pw = window.prompt("To change where your payouts go, enter your shop password:") || "";
                  if (!pw) { setError("Payout email not changed: your password is needed."); return; }
                }
                await shopSavePaymentSettings({ paypalEmail: paypalEmail.trim(), acceptCash, meetupAddress: acceptCash ? meetupAddress : "" }, pw);
                await loadShopData();
                setSuccess(pw ? "💰 Saved. New cash-outs open 48 hours after a payout email change." : "💰 Payment settings saved!"); setTimeout(() => setSuccess(""), 3000); }
              catch (e) { setError(e?.message || "Failed to save payment settings"); }
              finally { setSaving(false); }
            }}><Save size={18} /> {saving ? "Saving..." : "Save Payment Settings"}</button>
          </div>
        </div>
      )}

      {/* Danger Zone */}
      <div className="sset-danger-zone">
        <div className="sset-danger-title">Danger Zone</div>
        <button className="sset-danger-btn" onClick={logout}>
          <LogOut size={18} />
          Logout
        </button>
      </div>
    </div>
  );
}

/* ═══ Sub-components ═══ */

function ReadOnlyRow({ label, value, mono }) {
  return (
    <div className="sset-ro-row">
      <div className="sset-ro-label">{label}</div>
      <div className={`sset-ro-value ${mono ? "sset-mono" : ""}`}>{value || "—"}</div>
    </div>
  );
}

function SwitchRow({ label, desc, checked, onChange }) {
  return (
    <div className="sset-switch-row">
      <div className="sset-switch-info">
        <div className="sset-switch-label">{label}</div>
        <div className="sset-switch-desc">{desc}</div>
      </div>
      <label className="switch">
        <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
        <span className="slider"></span>
      </label>
    </div>
  );
}
